import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { ChevronRight, Download, X } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { LoanLimitsLookup } from '@/components/loan-limits';
import { EmployeeRoster } from '@/components/dashboard/EmployeeRoster';
import { TechnologyStatusBar } from '@/components/ui/TechnologyStatusBar';
import { LwHubCard, LwThemeScope } from '@/components/lw';
import { openArtemisWindow } from '@/utils/windowOpenHelper';
import {
  getHomeNavigationModel,
  getWithheldMarketWatchWidget,
  isHomeWidgetSection,
  type HomeNavigationCard,
  type HomeDestination
} from './homeNavigation';
import { getHomeDateLabel, getHomeGreeting } from './homePresentation';
import { useHomeWidgetsPreference } from './useHomeWidgetsPreference';
import './home.css';

const ENCOMPASS_DESKTOP_URL =
  'https://download.elliemae.com/encompass/install/smartclient/enc360/EncompassDesktop.exe';
const ENCOMPASS_CLIENT_ID = 'BE11391385';

interface HomeDashboardProps {
  currentRole: {
    name: string;
    color: string;
  };
  roleAccess: {
    hasAnyPermission: (permissions: string[]) => boolean;
    isLoadingPermissions?: boolean;
  };
  onSetActiveItem: (itemId: string) => void;
  launchers?: {
    artemis?: () => void;
    batcave?: () => void;
  };
  isCustomizing?: boolean;
  onCustomizingChange?: (isCustomizing: boolean) => void;
  isEffectiveAdmin?: boolean;
  loanOfficerHasAnyPermission?: (permissions: string[]) => boolean;
  isLoadingLoanOfficerPermissions?: boolean;
  canCustomizeHome?: boolean;
  now?: Date;
}

export const HomeDashboard: React.FC<HomeDashboardProps> = ({
  currentRole,
  roleAccess,
  onSetActiveItem,
  launchers,
  isCustomizing,
  onCustomizingChange,
  isEffectiveAdmin = false,
  loanOfficerHasAnyPermission,
  isLoadingLoanOfficerPermissions = false,
  canCustomizeHome = true,
  now = new Date()
}) => {
  const { user } = useAuth();
  const {
    removed,
    isResolved: isWidgetPreferenceResolved,
    isWritable: isWidgetPreferenceWritable,
    setRemoved
  } = useHomeWidgetsPreference();
  const loadedName = user?.firstName
    || user?.fullName
    || user?.user_metadata?.full_name
    || user?.user_metadata?.name
    || user?.email?.split('@')[0];
  const firstName = typeof loadedName === 'string'
    ? loadedName.trim().split(/\s+/)[0]
    : '';
  const greeting = `${getHomeGreeting(now)}${firstName ? `, ${firstName}` : ''}`;
  const navigation = getHomeNavigationModel(roleAccess.hasAnyPermission);
  const loanOfficerNavigation = useMemo(
    () => (
      loanOfficerHasAnyPermission
        ? getHomeNavigationModel(loanOfficerHasAnyPermission)
        : null
    ),
    [loanOfficerHasAnyPermission]
  );
  const loanOfficerCardIds = useMemo(
    () => new Set(
      loanOfficerNavigation?.sections.flatMap((section) => (
        section.cards.map((card) => card.id)
      )) ?? []
    ),
    [loanOfficerNavigation]
  );
  const removedWidgetNames = useMemo(() => new Set(removed), [removed]);
  const withheldMarketWatch = useMemo(getWithheldMarketWatchWidget, []);
  const MarketWatchIcon = withheldMarketWatch.icon;
  const [localIsCustomizing, setLocalIsCustomizing] = useState(false);
  const homeRootRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef<{
    cardId: string;
    target: 'add' | 'remove';
  } | null>(null);
  const effectiveIsCustomizing = canCustomizeHome
    && isWidgetPreferenceWritable
    && (isCustomizing ?? localIsCustomizing);
  const [hasMounted, setHasMounted] = useState(false);
  const isNavigationLoading = !hasMounted
    || roleAccess.isLoadingPermissions === true
    || !isWidgetPreferenceResolved;
  const isCustomizeUnavailable = isNavigationLoading
    || !isWidgetPreferenceWritable;

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useLayoutEffect(() => {
    const pendingFocus = pendingFocusRef.current;
    if (!pendingFocus) {
      return;
    }

    const target = homeRootRef.current?.querySelector<HTMLButtonElement>(
      `[data-home-${pendingFocus.target}="${pendingFocus.cardId}"]`
    );
    const fallback = homeRootRef.current?.querySelector<HTMLButtonElement>(
      '.lw-shell-home__customize'
    );
    (target ?? fallback)?.focus();
    pendingFocusRef.current = null;
  }, [removed]);

  const openDestination = (destination: HomeDestination) => {
    if (destination.kind === 'section') {
      onSetActiveItem(destination.id);
      return;
    }
    if (destination.kind === 'artemis') {
      (launchers?.artemis ?? openArtemisWindow)();
      return;
    }
    if (launchers?.batcave) {
      launchers.batcave();
      return;
    }
    window.location.href = '/batcave-admin';
  };

  const setCustomizing = (nextIsCustomizing: boolean) => {
    setLocalIsCustomizing(nextIsCustomizing);
    onCustomizingChange?.(nextIsCustomizing);
  };

  const removeWidget = (card: HomeNavigationCard) => {
    if (removedWidgetNames.has(card.title)) {
      return;
    }
    pendingFocusRef.current = { cardId: card.id, target: 'add' };
    setRemoved([...removed, card.title]);
  };

  const restoreWidget = (card: HomeNavigationCard) => {
    pendingFocusRef.current = { cardId: card.id, target: 'remove' };
    setRemoved(removed.filter((name) => name !== card.title));
  };

  const isAdminOnlyCard = (card: HomeNavigationCard) => (
    effectiveIsCustomizing
    && isEffectiveAdmin
    && !isLoadingLoanOfficerPermissions
    && loanOfficerNavigation !== null
    && !loanOfficerCardIds.has(card.id)
  );

  return (
    <LwThemeScope className="lw-ds-page lw-shell-home">
      <div ref={homeRootRef} className="wrap">
        <header className="lw-shell-home__header">
          <div className="min-w-0">
            <p className="eyebrow">LendWise Portal · {getHomeDateLabel(now)}</p>
            <div className="lw-shell-home__title-row">
              <h1 className="title">{greeting}</h1>
              <span className="lw-shell-home__role">{currentRole.name}</span>
            </div>
          </div>
          <div className="lw-shell-home__header-actions">
            <TechnologyStatusBar
              pageTitle=""
              className="lw-shell-home__technology"
              variant="compact"
            />
            {canCustomizeHome && (
              <button
                type="button"
                className="lw-shell-home__customize"
                aria-pressed={effectiveIsCustomizing}
                disabled={isCustomizeUnavailable}
                title={
                  isWidgetPreferenceResolved && !isWidgetPreferenceWritable
                    ? 'Home customization is unavailable until saved preferences load. Refresh to retry.'
                    : 'Add or remove home widgets'
                }
                onClick={() => setCustomizing(!effectiveIsCustomizing)}
              >
                {effectiveIsCustomizing ? 'Done' : '⊞ Customize'}
              </button>
            )}
          </div>
        </header>

        {effectiveIsCustomizing && !isNavigationLoading && (
          <aside
            className="lw-shell-home__customize-note"
            aria-label="Home customization instructions"
          >
            <strong>Customizing home</strong>
            <span>
              Remove a widget with its ✕; add it back from the tray under each section.
              Layouts save per person. Removing a card from Home does not change sidebar access.
              {isEffectiveAdmin && !isLoadingLoanOfficerPermissions && (
                <>
                  {' '}Cards marked <b>Admin only</b> never appear for a Loan Officer,
                  and the sidebar tags them too.
                </>
              )}
            </span>
          </aside>
        )}

        <section className="lw-shell-home__widgets" aria-label="Home resources">
          <LoanLimitsLookup className="lw-shell-home__card lw-shell-home__loan-limits" />

          <article className="lw-shell-home__card lw-shell-home__helpful">
            <header>
              <h2>Helpful Information</h2>
              <p>Setup links for the desktop stack.</p>
            </header>

            <div className="lw-shell-home__helpful-row">
              <div className="min-w-0">
                <h3>Encompass Desktop</h3>
                <p>SmartClient installer for loan origination</p>
              </div>
              <a
                href={ENCOMPASS_DESKTOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="lw-shell-home__download"
              >
                <Download aria-hidden="true" />
                Download
              </a>
            </div>

            <div className="lw-shell-home__helpful-row">
              <div className="min-w-0">
                <h3>Encompass Client ID</h3>
                <code>{ENCOMPASS_CLIENT_ID}</code>
              </div>
            </div>
          </article>
        </section>

        <div
          className="lw-shell-home__navigation"
          aria-busy={isNavigationLoading || undefined}
        >
          {isNavigationLoading ? (
            <div className="lw-shell-home__navigation-loading" role="status">
              Loading your workspace…
            </div>
          ) : (
            <>
              {navigation.sections.map((section) => {
                if (!isHomeWidgetSection(section.id)) {
                  return (
                    <section
                      key={section.id}
                      className={`lw-shell-home__nav-section lw-shell-home__nav-section--${section.id}`}
                      aria-labelledby={`home-${section.id}-heading`}
                      data-home-section={section.id}
                    >
                      <header className="lw-shell-home__section-header">
                        <h2 id={`home-${section.id}-heading`}>{section.title}</h2>
                        <p>{section.description}</p>
                        <span aria-hidden="true" />
                      </header>

                      <div className="lw-shell-home__tool-grid">
                        {section.cards.map((card) => {
                          const Icon = card.icon;
                          const showAdminOnly = isAdminOnlyCard(card);
                          return (
                            <button
                              key={card.id}
                              type="button"
                              className="lw-shell-home__tool-card"
                              aria-label={`Open ${card.title}${
                                showAdminOnly ? ' (Admin only)' : ''
                              }`}
                              data-home-card={card.id}
                              data-home-destination={card.destination.id}
                              onClick={() => openDestination(card.destination)}
                            >
                              <span className="lw-shell-home__tool-icon" aria-hidden="true">
                                <Icon />
                              </span>
                              <span className="lw-shell-home__tool-copy">
                                <strong className="lw-shell-home__tool-title">
                                  <span>{card.title}</span>
                                  {showAdminOnly && (
                                    <span className="lw-shell-home__admin-only">
                                      Admin only
                                    </span>
                                  )}
                                </strong>
                                <span>{card.description}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  );
                }

                const visibleCards = section.cards.filter(
                  (card) => !removedWidgetNames.has(card.title)
                );
                const removedCards = section.cards.filter(
                  (card) => removedWidgetNames.has(card.title)
                );

                if (!effectiveIsCustomizing && visibleCards.length === 0) {
                  return null;
                }

                const showWithheldMarketWatch = section.id === 'workspace' && isEffectiveAdmin;
                const trayLabel = `${removedCards.length} ${
                  removedCards.length === 1 ? 'widget' : 'widgets'
                } removed — click to add back`;

                return (
                  <section
                    key={section.id}
                    className={`lw-shell-home__nav-section lw-shell-home__nav-section--${section.id}${
                      effectiveIsCustomizing ? ' is-customizing' : ''
                    }`}
                    aria-labelledby={`home-${section.id}-heading`}
                    data-home-section={section.id}
                  >
                    <header className="lw-shell-home__section-header">
                      <h2 id={`home-${section.id}-heading`}>{section.title}</h2>
                      <p>{section.description}</p>
                      <span aria-hidden="true" />
                    </header>

                    <div className="lw-shell-home__card-grid">
                      {visibleCards.map((card) => {
                        const Icon = card.icon;
                        const showAdminOnly = isAdminOnlyCard(card);
                        return (
                          <div
                            key={card.id}
                            className={`lw-shell-home__widget-shell${
                              effectiveIsCustomizing ? ' is-editable' : ''
                            }`}
                          >
                            <LwHubCard
                              name={(
                                <span className="lw-shell-home__widget-title">
                                  <span>{card.title}</span>
                                  {showAdminOnly && (
                                    <span className="lw-shell-home__admin-only">
                                      Admin only
                                    </span>
                                  )}
                                </span>
                              )}
                              description={card.description}
                              icon={<Icon />}
                              className="lw-shell-home__nav-card"
                              aria-label={`Open ${card.title}${
                                showAdminOnly ? ' (Admin only)' : ''
                              }`}
                              data-home-card={card.id}
                              data-home-destination={card.destination.id}
                              onClick={() => openDestination(card.destination)}
                            />
                            {effectiveIsCustomizing && (
                              <button
                                type="button"
                                className="lw-shell-home__remove-widget"
                                aria-label={`Remove ${card.title} from Home`}
                                data-home-remove={card.id}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  removeWidget(card);
                                }}
                              >
                                <X aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        );
                      })}

                      {visibleCards.length === 0 && (
                        <div className="lw-shell-home__empty-section">
                          Every widget in this section is removed — add one back below.
                        </div>
                      )}
                    </div>

                    {effectiveIsCustomizing && (
                      <div
                        className="lw-shell-home__widget-tray"
                        aria-label={`${section.title} removed widgets`}
                      >
                        <p>{trayLabel}</p>
                        <div>
                          {removedCards.map((card) => {
                            const Icon = card.icon;
                            return (
                              <button
                                key={card.id}
                                type="button"
                                aria-label={`Add ${card.title} back to Home`}
                                data-home-add={card.id}
                                onClick={() => restoreWidget(card)}
                              >
                                <Icon aria-hidden="true" />
                                <span>{card.title}</span>
                                <b aria-hidden="true">＋</b>
                              </button>
                            );
                          })}

                          {showWithheldMarketWatch && (
                            <button
                              type="button"
                              className="is-withheld"
                              aria-label={`${withheldMarketWatch.title} — TBD`}
                              disabled
                              data-home-withheld={withheldMarketWatch.id}
                            >
                              <MarketWatchIcon aria-hidden="true" />
                              <span>{withheldMarketWatch.title} — TBD</span>
                              <b aria-hidden="true">＋</b>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </section>
                );
              })}

              {navigation.reports.length > 0 && (
                <section
                  className="lw-shell-home__nav-section lw-shell-home__reports"
                  aria-labelledby="home-company-reports-heading"
                  data-home-section="company-reports"
                >
                  <header className="lw-shell-home__section-header">
                    <h2 id="home-company-reports-heading">Company Reports</h2>
                    <span aria-hidden="true" />
                  </header>
                  <div className="lw-shell-home__report-list">
                    {navigation.reports.map((report) => (
                      <button
                        key={report.id}
                        type="button"
                        aria-label={`Open ${report.title}`}
                        data-home-card={report.id}
                        data-home-destination={report.destination.id}
                        onClick={() => openDestination(report.destination)}
                      >
                        <strong>{report.title}</strong>
                        <span>
                          Open
                          <ChevronRight aria-hidden="true" />
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        <EmployeeRoster className="lw-shell-home__roster" />
      </div>
    </LwThemeScope>
  );
};

export default HomeDashboard;
